import type { LobbyState, WSClientMsg, WSServerMsg } from '@geoguess/shared';
import { WS_PATH } from './discord.js';

export type WSConnection = {
  send: (msg: WSClientMsg) => void;
  close: () => void;
};

type Handlers = {
  onState?: (lobby: LobbyState) => void;
  onMessage?: (msg: WSServerMsg) => void;
  onOpen?: () => void;
  onClose?: () => void;
};

export function connect(hello: Extract<WSClientMsg, { t: 'hello' }>, h: Handlers): WSConnection {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const url = `${proto}//${window.location.host}${WS_PATH}`;
  let ws: WebSocket | null = new WebSocket(url);
  let closed = false;
  let pingTimer: number | null = null;
  let reconnectAttempt = 0;

  const open = (sock: WebSocket) => {
    sock.send(JSON.stringify(hello));
    h.onOpen?.();
    pingTimer = window.setInterval(() => {
      if (sock.readyState === sock.OPEN) sock.send(JSON.stringify({ t: 'ping' }));
    }, 25_000);
  };

  const wire = (sock: WebSocket) => {
    sock.addEventListener('open', () => open(sock));
    sock.addEventListener('message', (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as WSServerMsg;
        if (msg.t === 'state') h.onState?.(msg.lobby);
        h.onMessage?.(msg);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[ws] bad message', err);
      }
    });
    sock.addEventListener('close', () => {
      h.onClose?.();
      if (pingTimer) {
        clearInterval(pingTimer);
        pingTimer = null;
      }
      if (closed) return;
      reconnectAttempt += 1;
      const delay = Math.min(8000, 500 * 2 ** reconnectAttempt);
      setTimeout(() => {
        if (closed) return;
        ws = new WebSocket(url);
        wire(ws);
      }, delay);
    });
    sock.addEventListener('error', () => {
      // close handler will run too
    });
  };

  wire(ws);

  return {
    send: (msg) => {
      if (ws && ws.readyState === ws.OPEN) ws.send(JSON.stringify(msg));
    },
    close: () => {
      closed = true;
      if (pingTimer) clearInterval(pingTimer);
      ws?.close();
    },
  };
}
